import { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  TextField,
  Chip,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  faUsers,
  faLayerGroup,
  faDollarSign,
  faBoxOpen,
  faFilter,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../../contexts/ThemeContext";
import { useDonorInventoryData } from "../../hooks/useDonorInventoryData";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import config from "../../config";
import { isMasterAdmin } from "../../utils/auth";

import Card from "../../components/ui/Card";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Alert from "../../components/ui/Alert";
import ActionButton from "../../components/ui/ActionButton";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";

import MaterialsOverviewSection from "../../components/DonorInventory/MaterialsOverviewSection";
import TopReceiversChart from "../../components/DonorInventory/TopReceiversChart";
import AllocationTrendChart from "../../components/DonorInventory/AllocationTrendChart";
import TransactionsTable from "../../components/DonorInventory/TransactionsTable";
import DonorMFCASummary from "../../components/DonorInventory/DonorMFCASummary";
import DonorOpportunityBreakdown from "../../components/DonorInventory/DonorOpportunityBreakdown";
import DonorMatchingInsights from "../../components/DonorInventory/DonorMatchingInsights";
import DonorCostBreakdown from "../../components/DonorInventory/DonorCostBreakdown";

import "../../style/DonorInventory.css";

// accordion wrapper component
const InventoryAccordion = ({
  id,
  title,
  children,
  defaultExpanded = true,
}) => {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      className="donor-inv-accordion"
      disableGutters
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        className="donor-inv-accordion-header"
      >
        <Typography className="donor-inv-accordion-title">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails className="donor-inv-accordion-content">
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

function DonorInventory() {
  const { t } = useTranslation();
  const { hasPermission, isChecking } = usePermissionGuard("donor", "inventory");
  const [dateRange, setDateRange] = useState(undefined); // undefined = loading

  useEffect(() => {
    const params = new URLSearchParams();
    if (isMasterAdmin()) params.set("show_all", "true");

    fetch(`${config.apiUrl}/donor/inventory/date-range?${params}`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    })
      .then((r) => r.json())
      .then((res) =>
        setDateRange(res?.min_date && res?.max_date ? res : null)
      )
      .catch(() => setDateRange(null));
  }, []);

  if (isChecking || dateRange === undefined) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Loading inventory data...")}
      />
    );
  }
  if (!hasPermission) return null;

  return <DonorInventoryContent dateRange={dateRange} />;
}

function DonorInventoryContent({ dateRange }) {
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();

  const [isVisible, setIsVisible] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);
  const prevRefreshingRef = useRef(false);
  const justUpdatedTimerRef = useRef(null);

  // default filter values — seeded from date-range prop, falls back to 30 days
  const defaultDateTo = useMemo(() => {
    return dateRange?.max_date || new Date().toISOString().slice(0, 10);
  }, [dateRange]);
  const defaultDateFrom = useMemo(() => {
    if (dateRange?.min_date) return dateRange.min_date;
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, [dateRange]);
  const defaultPeriod = "D";

  // filter state
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [period, setPeriod] = useState(defaultPeriod);
  const [selectedMaterials, setSelectedMaterials] = useState([]);

  // check if filters have been modified from defaults
  const isModified =
    dateFrom !== defaultDateFrom ||
    dateTo !== defaultDateTo ||
    period !== defaultPeriod ||
    selectedMaterials.length > 0;

  // reset filters to defaults
  const handleReset = () => {
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setPeriod(defaultPeriod);
    setSelectedMaterials([]);
  };

  const filters = useMemo(
    () => ({
      dateFrom,
      dateTo,
      period,
      materials: selectedMaterials,
    }),
    [dateFrom, dateTo, period, selectedMaterials],
  );

  // fetch data (auto-updates when filters change)
  const {
    loading,
    refreshing,
    error,
    counters,
    materialBreakdown,
    topReceivers,
    receiversByMaterial,
    timeline,
    transactions,
    availableMaterials,
    expiredListings,
    acceptanceSummary,
    refresh,
    lastUpdated,
  } = useDonorInventoryData(filters);

  // trigger slide-up animation when refresh completes
  useEffect(() => {
    // Detect transition from refreshing=true to refreshing=false
    if (prevRefreshingRef.current && !refreshing) {
      setJustUpdated(true);
      justUpdatedTimerRef.current = setTimeout(() => {
        setJustUpdated(false);
      }, 500);
    }
    prevRefreshingRef.current = refreshing;

    return () => {
      if (justUpdatedTimerRef.current)
        clearTimeout(justUpdatedTimerRef.current);
    };
  }, [refreshing]);

  // trigger fade-in animation after loading completes
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // counter cards configuration
  const counterCards = [
    {
      icon: faUsers,
      value: counters.uniqueReceivers,
      title: t("Unique Receivers"),
      description: t("Companies you've supplied"),
      colorClass: "counter-blue",
    },
    {
      icon: faLayerGroup,
      value: counters.materialTypes,
      title: t("Material Types"),
      description: t("Different materials donated"),
      colorClass: "counter-amber",
    },
    {
      icon: faBoxOpen,
      value: `${counters.totalQuantity.toLocaleString()} lbs`,
      title: t("Quantity Allocated"),
      description: t("Total materials distributed"),
      colorClass: "counter-green",
    },
    {
      icon: faDollarSign,
      value: `$${counters.totalRevenue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      title: t("Total Revenue"),
      description: t("From allocated materials"),
      colorClass: "counter-purple",
    },
  ];

  if (loading) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Loading inventory data...")}
      />
    );
  }

  return (
    <div
      className={`donor-inventory-container ${darkMode ? "dark" : "light"} ${
        isVisible ? "visible" : ""
      }`}
    >
      {/* Page header with last updated badge */}
      <div className="page-header-row">
        <div /> {/* Spacer */}
        <LastUpdatedBadge
          timestamp={lastUpdated}
          onRefresh={() => refresh(filters)}
          isRefreshing={refreshing}
        />
      </div>

      {/* Error Alert */}
      {error && (
        <div className="donor-inv-content-wrapper">
          <Alert message={error} severity="error" />
        </div>
      )}

      {/* Filters Bar */}
      <div className="donor-inv-filters-wrapper">
        <div className="donor-inv-filters">
          {/* Date Range */}
          <Box className="donor-filter-group">
            <Typography variant="caption" className="donor-filter-label">
              {t("Date Range")}
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                type="date"
                size="small"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="donor-filter-input date-input"
                inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
              />
              <TextField
                type="date"
                size="small"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="donor-filter-input date-input"
                inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
              />
            </Stack>
          </Box>

          {/* Period */}
          <Box className="donor-filter-group">
            <Typography variant="caption" className="donor-filter-label">
              {t("Period")}
            </Typography>
            <Select
              size="small"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="donor-filter-input period-select"
              MenuProps={{
                disablePortal: false,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              }}
              sx={{
                "& .MuiSelect-select": {
                  color: darkMode ? "#fff" : "#1a1a1a",
                },
              }}
            >
              <MenuItem value="D">{t("Daily")}</MenuItem>
              <MenuItem value="W">{t("Weekly")}</MenuItem>
              <MenuItem value="M">{t("Monthly")}</MenuItem>
              <MenuItem value="Y">{t("Yearly")}</MenuItem>
            </Select>
          </Box>

          {/* Material Type Filter */}
          <Box
            className="donor-filter-group donor-filter-group-grow"
            sx={{ flex: 1 }}
          >
            <Typography variant="caption" className="donor-filter-label">
              {t("Material Type")}
            </Typography>
            <Select
              multiple
              size="small"
              value={selectedMaterials}
              onChange={(e) => setSelectedMaterials(e.target.value)}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <span className="donor-filter-placeholder">
                    {t("All Materials")}
                  </span>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((s) => (
                      <Chip key={s} label={s} size="small" />
                    ))}
                  </Box>
                )
              }
              className="donor-filter-input material-select"
              MenuProps={{
                disablePortal: false,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              }}
              sx={{
                width: "100%",
                "& .MuiSelect-select": {
                  color: darkMode ? "#fff" : "#1a1a1a",
                },
              }}
            >
              {availableMaterials.map((mat) => (
                <MenuItem key={mat} value={mat}>
                  {mat}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Reset Button */}
          {isModified && (
            <ActionButton
              variant="default"
              icon={faUndo}
              onClick={handleReset}
              disabled={refreshing}
            >
              {t("Reset")}
            </ActionButton>
          )}

          {/* Apply Filters Button */}
          <ActionButton
            variant="secondary"
            icon={faFilter}
            onClick={() => refresh(filters)}
            disabled={refreshing}
            spinning={refreshing}
          >
            {t("Apply")}
          </ActionButton>
        </div>
      </div>

      {/* Counter Cards */}
      <div
        className={`donor-inv-counters-wrapper ${
          refreshing ? "is-refreshing shimmer-overlay" : ""
        }`}
      >
        <div className="counter-cards-container">
          {counterCards.map((card, index) => (
            <Card
              key={index}
              icon={card.icon}
              value={card.value}
              title={card.title}
              description={card.description}
              variant="counter"
              colorClass={card.colorClass}
              darkMode={darkMode}
              isLoading={refreshing}
              justUpdated={justUpdated}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`donor-inv-content-wrapper ${
          refreshing ? "is-refreshing shimmer-overlay" : ""
        }`}
      >
        {/* Allocation Summary Section */}
        <InventoryAccordion
          id="allocation-summary"
          title={t("Allocation Summary")}
        >
          <MaterialsOverviewSection
            data={materialBreakdown}
            transactions={transactions}
            darkMode={darkMode}
          />
        </InventoryAccordion>

        {/* Top Receivers Section */}
        <InventoryAccordion id="top-receivers" title={t("Top Receivers")}>
          <TopReceiversChart
            topReceivers={topReceivers}
            receiversByMaterial={receiversByMaterial}
            availableMaterials={availableMaterials}
            darkMode={darkMode}
          />
        </InventoryAccordion>

        {/* Trends & History Section */}
        <InventoryAccordion id="trends-history" title={t("Trends & History")}>
          <AllocationTrendChart
            timeline={timeline}
            period={period}
            darkMode={darkMode}
          />
        </InventoryAccordion>

        {/* Transaction History Section */}
        <InventoryAccordion
          id="transaction-history"
          title={t("Transaction History")}
        >
          <TransactionsTable transactions={transactions} darkMode={darkMode} />
        </InventoryAccordion>

        {/* Circularity Impact Section (MFCA) */}
        <InventoryAccordion
          id="circularity-impact"
          title={t("Circularity Impact (MFCA)")}
        >
          <DonorMFCASummary
            transactions={transactions}
            expiredListings={expiredListings}
            darkMode={darkMode}
            isRefreshing={refreshing}
            justUpdated={justUpdated}
          />
          <Stack direction="row" spacing={2} sx={{ width: "100%", mt: 3, pt: 2, borderTop: "1px solid var(--border-color)" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DonorOpportunityBreakdown
                transactions={transactions}
                expiredListings={expiredListings}
                darkMode={darkMode}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DonorMatchingInsights
                acceptanceSummary={acceptanceSummary}
                darkMode={darkMode}
              />
            </Box>
          </Stack>
          <DonorCostBreakdown
            transactions={transactions}
            expiredListings={expiredListings}
            darkMode={darkMode}
          />
        </InventoryAccordion>
      </div>
    </div>
  );
}

export default DonorInventory;
